import React, { useState } from 'react';
import {
  Typography,
  Box,
  IconButton,
  TextField,
  Button,
  Paper,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { usePositions } from './PositionsContext';

const PositionsSettings = () => {
  const { positions, shifts, addPosition, removePosition, addShift, removeShift } = usePositions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [newPosition, setNewPosition] = useState('');
  const [newShift, setNewShift] = useState('');
  const [positionError, setPositionError] = useState('');
  const [shiftError, setShiftError] = useState('');

  const handleAddPosition = () => {
    if (!newPosition.trim()) {
      setPositionError('Το πεδίο δεν μπορεί να είναι κενό');
      return;
    }
    if (positions.includes(newPosition)) {
      setPositionError('Αυτό το πόστο υπάρχει ήδη');
      return;
    }
    addPosition(newPosition);
    setNewPosition('');
    setPositionError('');
  };

  const handleAddShift = () => {
    if (!newShift.trim()) {
      setShiftError('Το πεδίο δεν μπορεί να είναι κενό');
      return;
    }
    if (shifts.includes(newShift)) {
      setShiftError('Αυτή η βάρδια υπάρχει ήδη');
      return;
    }
    addShift(newShift);
    setNewShift('');
    setShiftError('');
  };

  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter') handler();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Positions Section */}
      <Paper sx={{
        borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.01)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WorkIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Πόστα
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {positions.length} {positions.length === 1 ? 'πόστο' : 'πόστα'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Add input */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <TextField
              placeholder="Νέο πόστο..."
              value={newPosition}
              onChange={(e) => { setNewPosition(e.target.value); setPositionError(''); }}
              onKeyDown={(e) => handleKeyDown(e, handleAddPosition)}
              error={!!positionError}
              helperText={positionError}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WorkIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddPosition}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                minWidth: isMobile ? 'auto' : 130, px: isMobile ? 2 : 3,
                bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' },
                boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
              }}
            >
              {isMobile ? '' : 'Προσθήκη'}
            </Button>
          </Box>

          {/* Position chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            {positions.map((position) => (
              <Chip
                key={position}
                label={position}
                onDelete={positions.length > 1 ? () => removePosition(position) : undefined}
                deleteIcon={
                  <Tooltip title="Διαγραφή">
                    <DeleteIcon sx={{ fontSize: '16px !important' }} />
                  </Tooltip>
                }
                sx={{
                  height: 36, borderRadius: 2, fontWeight: 500, fontSize: '0.85rem',
                  bgcolor: 'rgba(59,130,246,0.08)', color: '#1d4ed8',
                  border: '1px solid rgba(59,130,246,0.2)',
                  '& .MuiChip-deleteIcon': {
                    color: '#93c5fd',
                    '&:hover': { color: '#ef4444' },
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Shifts Section */}
      <Paper sx={{
        borderRadius: 3, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Box sx={{
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          bgcolor: 'rgba(0,0,0,0.01)',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ScheduleIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Βάρδιες
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {shifts.length} {shifts.length === 1 ? 'βάρδια' : 'βάρδιες'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Add input */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
            <TextField
              placeholder="Νέα βάρδια..."
              value={newShift}
              onChange={(e) => { setNewShift(e.target.value); setShiftError(''); }}
              onKeyDown={(e) => handleKeyDown(e, handleAddShift)}
              error={!!shiftError}
              helperText={shiftError}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ScheduleIcon sx={{ fontSize: 18, color: '#999' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f59e0b' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f59e0b' },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddShift}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                minWidth: isMobile ? 'auto' : 130, px: isMobile ? 2 : 3,
                bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' },
                boxShadow: '0 4px 14px rgba(245,158,11,0.3)',
              }}
            >
              {isMobile ? '' : 'Προσθήκη'}
            </Button>
          </Box>

          {/* Shift chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            {shifts.map((shift) => (
              <Chip
                key={shift}
                label={shift}
                onDelete={shifts.length > 1 ? () => removeShift(shift) : undefined}
                deleteIcon={
                  <Tooltip title="Διαγραφή">
                    <DeleteIcon sx={{ fontSize: '16px !important' }} />
                  </Tooltip>
                }
                sx={{
                  height: 36, borderRadius: 2, fontWeight: 500, fontSize: '0.85rem',
                  bgcolor: 'rgba(245,158,11,0.08)', color: '#92400e',
                  border: '1px solid rgba(245,158,11,0.2)',
                  '& .MuiChip-deleteIcon': {
                    color: '#fcd34d',
                    '&:hover': { color: '#ef4444' },
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default PositionsSettings;
